import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  createMotive,
  CreateMotiveProps,
  DeleteMotiveProps,
  deleteMotives,
  getAllMotivos,
  updateMotive,
  UpdateMotiveProps,
} from "@/services/motives.service";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormGetValues } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { MotivoInList } from "./motives.schema";

export interface CreateMotiveHandlerProps {
  setLoadingCreateMotive: Dispatch<SetStateAction<boolean>>;
  getValues: UseFormGetValues<FieldValues>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleCreateMotiveHandler = async ({
  setLoadingCreateMotive,
  getValues,
  navigate,
  fetchPermissions,
}: CreateMotiveHandlerProps) => {
  try {
    setLoadingCreateMotive(true);

    const values = getValues();

    const objToSend: CreateMotiveProps = {
      descripcion: values?.descripcion,
      idsTipoSolicitud: values?.tipoSolicitud,
      tiposMotivo: values?.tipoMotivo,
    };

    await createMotive(objToSend);

    toaster.create({
      type: "success",
      title: "Motivo creado con éxito.",
    });

    navigate(AppRoutes.MOTIVES);

    setLoadingCreateMotive(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.MOTIVES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo actualizar el motivo",
      });
      throw error;
    }

    throw error;
  } finally {
    setLoadingCreateMotive(false);
  }
};

export interface UpdateMotiveHandlerProps {
  setLoadingUpdateMotive: Dispatch<SetStateAction<boolean>>;
  getValues: UseFormGetValues<FieldValues>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  id: number;
}

export const handleUpdateMotiveHandler = async ({
  getValues,
  navigate,
  setLoadingUpdateMotive,
  fetchPermissions,
  id,
}: UpdateMotiveHandlerProps) => {
  try {
    setLoadingUpdateMotive(true);

    const values = getValues();
    const objToSend: UpdateMotiveProps = {
      descripcion: values?.descripcion,
      idsTipoSolicitud: values?.tipoSolicitud,
      tiposMotivo: values?.tipoMotivo,
      id,
    };

    await updateMotive(objToSend);

    toaster.create({
      type: "success",
      title: "Motivo actualizado con éxito.",
    });

    navigate(AppRoutes.MOTIVES);

    setLoadingUpdateMotive(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.MOTIVES,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo actualizar el motivo",
      });
      throw error;
    }

    throw error;
  } finally {
    setLoadingUpdateMotive(false);
  }
};

export interface HandleDeleteMotivesHandlerProps {
  setLoadingDeleteMotives: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: (value: React.SetStateAction<boolean>) => void;
  selection: string[];
  fetchMotives: (showLoad?: boolean) => Promise<void>;
  setSelection: Dispatch<React.SetStateAction<string[]>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const handleDeleteMotivesHandler = async ({
  setLoadingDeleteMotives,
  setOpenConfirm,
  fetchMotives,
  setSelection,
  selection,
  navigate,
  fetchPermissions,
}: HandleDeleteMotivesHandlerProps) => {
  try {
    setLoadingDeleteMotives(true);

    const objToSend: DeleteMotiveProps = {
      idMotivos: selection.map(Number),
    };

    await deleteMotives(objToSend);

    toaster.create({
      type: "success",
      title: "Motivos seleccionados eliminadas con éxito.",
    });

    setOpenConfirm(false);
    setLoadingDeleteMotives(false);

    setSelection([]);

    await fetchMotives(false);
  } catch (error) {
    const errorObj = error as AxiosError<{
      message: string;
      tiposFaltantes?: string[];
    }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron eliminar los motivos seleccionados.",
      });
    }

    throw error;
  } finally {
    setLoadingDeleteMotives(false);
    setOpenConfirm(false);
  }
};

interface FetchMotivesHandlerProps {
  setLoadingMotivos: Dispatch<SetStateAction<boolean>>;
  setMotivos: Dispatch<SetStateAction<MotivoInList[] | null | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
}

export const fetchMotivesHandler = async ({
  setLoadingMotivos,
  setMotivos,
  navigate,
  fetchPermissions,
  showLoad,
}: FetchMotivesHandlerProps) => {
  try {
    if (showLoad) setLoadingMotivos(true);

    const response = await getAllMotivos();

    setMotivos(response);

    setLoadingMotivos(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudieron obtener los motivos.",
      });
      throw error;
    }
  }
};
